import AddIcon from '@mui/icons-material/Add'
import CachedIcon from '@mui/icons-material/Cached'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Autocomplete, Box, Button, Card, CardContent, CardHeader, Chip, Dialog, Grid, IconButton, TextField, Tooltip } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { CountryAvatar } from 'src/components/icons/CountryAvatar'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { ItecDataGrid, ItecGridColsDef as GridColsDef, useItecDataGrid } from 'src/lib/itaptec/datagrid/src'
import { EntityListWithPaginationFields, EntityPaginationFields, EntityWithIdAndDescription, EntityWithIdAndDescriptionFields, PaisLogo, PaisLogoFields } from 'src/types'
import { Sistema } from 'src/pages/messages/types'
import { WrapperTable } from '../../components'
import { HttpEtapasABM } from '../api/httpEtapasABM'
import { EtapaRequestFields, EtapaViewFields } from '../api/schemas'
import { Etapa } from '../types'
import { DialogEtapaABM } from './DialogEtapaABM'

const emptyPage = (actualPage: number, pageSize: number) =>
  Promise.resolve({
    [EntityListWithPaginationFields.List]: [],
    [EntityListWithPaginationFields.Pagination]: {
      [EntityPaginationFields.ActualPage]: actualPage,
      [EntityPaginationFields.PageSize]: pageSize,
      [EntityPaginationFields.CantPages]: 0,
      [EntityPaginationFields.CantRecords]: 0
    }
  })

export const TablaEtapasABM = () => {
  const [dialogNewOpen, setDialogNewOpen] = useState<boolean>(false)
  const [dialogEditOpen, setDialogEditOpen] = useState<Etapa>()
  const [paisSeleccionado, setPaisSeleccionado] = useState<PaisLogo | null>(null)
  const [sistemaSeleccionado, setSistemaSeleccionado] = useState<EntityWithIdAndDescription | null>(null)
  const [tipoSistemaSeleccionado, setTipoSistemaSeleccionado] = useState<Sistema | null>(null)
  const [sistemas, setSistemas] = useState<EntityWithIdAndDescription[]>([])
  const [tiposSistema, setTiposSistema] = useState<Sistema[]>([])
  const { paisLogos } = usePaisLogo()
  const { askConfirmation } = useConfirmationDialog()

  const filtrosAplicados = !!paisSeleccionado && (!!sistemaSeleccionado || !!tipoSistemaSeleccionado)

  useEffect(() => {
    HttpCacheWorkflow.getSistemasTipos().then(setTiposSistema)
  }, [])

  useEffect(() => {
    setSistemaSeleccionado(null)
    if (!paisSeleccionado) {
      setSistemas([])

      return
    }

    HttpCacheWorkflow.getSistemasPorSistemaPrincipal(paisSeleccionado[PaisLogoFields.CodPais]).then(setSistemas)
  }, [paisSeleccionado])

  const columnas: GridColsDef<Etapa>[] = [
    { headerName: 'Id', field: 'id', width: 90 },
    { headerName: 'Id virtual', field: EtapaViewFields.IdEtapaVirtual, width: 110, type: 'number' },
    { headerName: 'Nombre', field: EtapaViewFields.NombreEtapa, flex: 2, minWidth: 180 },
    {
      headerName: 'Estado',
      field: EtapaViewFields.DescEtapaEstado,
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) => (
        <Chip
          size='small'
          label={row[EtapaViewFields.DescEtapaEstado] || row[EtapaViewFields.CodEtapaEstado]}
          sx={{
            borderColor: row[EtapaViewFields.HexColorEtapaEstado] || undefined,
            color: row[EtapaViewFields.HexColorEtapaEstado] || undefined
          }}
          variant='outlined'
        />
      )
    },
    {
      headerName: 'Sistema / Pais',
      field: EtapaViewFields.NombreSistema,
      flex: 2,
      minWidth: 220,
      renderCell: ({ row }) => (
        <Chip
          label={`${row[EtapaViewFields.NombreSistema] || row[EtapaViewFields.CodSistema]} - ${row[EtapaViewFields.NombreSistemaPrincipal] || row[EtapaViewFields.CodSistemaPrincipal]}`}
          size='small'
          variant='outlined'
          avatar={<CountryAvatar countryCode={row[EtapaViewFields.CodSistemaPrincipal]} />}
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => getActions(row, true)
    }
  ]

  const getActions = (etapa: Etapa, small: boolean) => [
    <GridActionsCellItem
      onClick={() => {
        const promise = HttpEtapasABM.getEtapa(etapa.id)

        toast
          .promise(promise, {
            success: 'Cargado.',
            loading: 'Cargando.',
            error: 'Error.'
          })
          .then(setDialogEditOpen)
      }}
      icon={
        <Tooltip title='Editar'>
          <EditIcon color='info' />
        </Tooltip>
      }
      label='Editar'
      showInMenu={small}
    />,
    <GridActionsCellItem
      onClick={() =>
        askConfirmation(() => {
          const promise = HttpEtapasABM.deleteEtapa(etapa.id)

          toast
            .promise(promise, {
              success: 'Eliminado',
              error: 'No se pudo eliminar',
              loading: 'Eliminando'
            })
            .then(() => refetch())
        })
      }
      icon={
        <Tooltip title='Eliminar'>
          <DeleteIcon color='error' />
        </Tooltip>
      }
      label='Eliminar'
      showInMenu={small}
    />
  ]

  const {
    datagridProps,
    control: {
      query: { refetch }
    }
  } = useItecDataGrid({
    pagination: true,
    columns: columnas,
    getEntitiesPaginated: (pagination) => {
      if (!filtrosAplicados) return emptyPage(pagination.actualPage, pagination.pageSize)

      return HttpEtapasABM.getEtapas(pagination, {
        [EtapaRequestFields.CodSistemaPrincipal]: paisSeleccionado?.[PaisLogoFields.CodPais],
        [EtapaRequestFields.CodSistema]: sistemaSeleccionado?.[EntityWithIdAndDescriptionFields.Id],
        [EtapaRequestFields.CodSistemaTipo]: tipoSistemaSeleccionado?.[EntityWithIdAndDescriptionFields.Id]
      })
    },
    density: 'compact'
  })

  useEffect(() => {
    refetch()
  }, [paisSeleccionado, sistemaSeleccionado, tipoSistemaSeleccionado])

  const paises = useMemo(
    () =>
      paisLogos.map(p => ({
        ...p,
        id: p[PaisLogoFields.CodPais]
      })),
    [paisLogos]
  )

  return (
    <WrapperTable>
      <Card>
        <CardHeader
          title='Administracion de etapas'
          action={
            <Box display='flex' gap={2}>
              <Button startIcon={<AddIcon />} variant='contained' onClick={() => setDialogNewOpen(true)}>
                Nueva etapa
              </Button>
              <IconButton disabled={datagridProps.loading} onClick={() => refetch()}>
                <CachedIcon />
              </IconButton>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={paises}
                value={paisSeleccionado}
                onChange={(_, value) => setPaisSeleccionado(value)}
                getOptionLabel={option => option[EntityWithIdAndDescriptionFields.Description]}
                renderInput={params => <TextField {...params} label='Pais' size='small' />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={sistemas}
                value={sistemaSeleccionado}
                onChange={(_, value) => setSistemaSeleccionado(value)}
                getOptionLabel={option => option[EntityWithIdAndDescriptionFields.Description]}
                disabled={!paisSeleccionado}
                renderInput={params => <TextField {...params} label='Sistema' size='small' />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={tiposSistema}
                value={tipoSistemaSeleccionado}
                onChange={(_, value) => setTipoSistemaSeleccionado(value)}
                getOptionLabel={option => option[EntityWithIdAndDescriptionFields.Description]}
                renderInput={params => <TextField {...params} label='Sistema tipo' size='small' />}
              />
            </Grid>
          </Grid>
        </CardContent>
        <ItecDataGrid {...datagridProps} />
      </Card>
      <Dialog open={dialogNewOpen} fullWidth maxWidth='lg'>
        {dialogNewOpen && (
          <DialogEtapaABM
            closeDialog={() => {
              setDialogNewOpen(false)
              refetch()
            }}
          />
        )}
      </Dialog>
      <Dialog open={!!dialogEditOpen} fullWidth maxWidth='lg'>
        {!!dialogEditOpen && (
          <DialogEtapaABM
            closeDialog={() => {
              setDialogEditOpen(undefined)
              refetch()
            }}
            etapa={dialogEditOpen}
          />
        )}
      </Dialog>
    </WrapperTable>
  )
}
